import React, { useEffect, useState } from 'react';
import { ArrowRight, ChevronRight, Smartphone, X } from 'lucide-react';
import { CleroS } from './Logo';

interface WebAppTakeoverBannerProps {
    sessionToken: string;
    appDeepLink?: string;
    sessionId?: string;
    merchantName?: string;
    amount?: number;
}

export const WebAppTakeoverBanner: React.FC<WebAppTakeoverBannerProps> = ({
    sessionToken,
    appDeepLink,
    sessionId,
    merchantName = 'Clero',
    amount,
}) => {
    const storageKey = `clero_takeover_dismissed_${sessionId || sessionToken || 'default'}`;
    const [isDismissed, setIsDismissed] = useState(true);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const ua = navigator.userAgent || '';
        const isMobileUA = /iPhone|iPod|Android|Mobile/i.test(ua);
        const isTabletOrTouch = /iPad/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const isSmallScreen = window.innerWidth <= 768;
        const mobileCheck = isMobileUA || isTabletOrTouch || isSmallScreen;
        setIsMobile(mobileCheck);

        try {
            const dismissed = sessionStorage.getItem(storageKey) || localStorage.getItem(storageKey);
            if (!dismissed && mobileCheck) {
                setIsDismissed(false);
            }
        } catch {
            if (mobileCheck) {
                setIsDismissed(false);
            }
        }
    }, [storageKey]);

    const handleDismiss = () => {
        setIsDismissed(true);
        try {
            sessionStorage.setItem(storageKey, 'true');
        } catch {
            // Ignore storage errors
        }
    };

    if (isDismissed || !isMobile) return null;

    const targetUrl = appDeepLink || `clero://pay/${sessionToken}`;

    return (
        <div className="fixed inset-0 z-[100] bg-slate-950/90 backdrop-blur-md flex flex-col justify-between p-6 animate-in fade-in duration-200">
            {/* Top Bar with Dismiss */}
            <div className="flex items-center justify-end">
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                    aria-label="Continue in browser"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Center Content */}
            <div className="flex flex-col items-center text-center max-w-xs mx-auto space-y-6 my-auto">
                <div className="w-20 h-20 rounded-3xl bg-teal-600 flex items-center justify-center shadow-2xl shadow-teal-500/30 ring-1 ring-white/20">
                    <CleroS size={44} color="#FFFFFF" />
                </div>

                <div className="space-y-2">
                    <h2 className="text-2xl font-black tracking-tight text-white">
                        Open in Clero
                    </h2>
                    <p className="text-sm font-medium text-slate-300 leading-relaxed">
                        You already have Clero. Continue in the app to settle your share.
                    </p>
                </div>
            </div>

            {/* Bottom Actions */}
            <div className="flex flex-col gap-3 max-w-sm w-full mx-auto pb-4">
                <a
                    href={targetUrl}
                    className="w-full py-4 rounded-full bg-teal-600 hover:bg-teal-500 text-white font-extrabold text-base flex items-center justify-center gap-2 transition-transform active:scale-[0.98] shadow-lg shadow-teal-900/30 text-center"
                >
                    <span>Open in Clero</span>
                    <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                </a>

                <button
                    type="button"
                    onClick={handleDismiss}
                    className="w-full py-3 text-center text-xs font-bold text-slate-400 hover:text-white transition-colors"
                >
                    Continue in web browser
                </button>
            </div>
        </div>
    );
};

export default WebAppTakeoverBanner;
